/**
 * Baydoun Footwear — filter bar
 *
 * Progressive enhancement over a plain GET form: without JS the form still
 * submits and Shopify renders the filtered page. With JS we swap the filter bar
 * and the product grid in place using the Section Rendering API.
 */
(function () {
  'use strict';

  const SECTION_ATTR = 'data-baydoun-facets-section';
  const MOBILE_MQ = '(max-width: 989px)';
  let activeRequest = null;
  let applying = false;

  function sectionRoot(el) {
    return el.closest('[' + SECTION_ATTR + ']');
  }

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function normalizeUrl(href) {
    const url = new URL(href, window.location.origin);
    return url.pathname + url.search;
  }

  function includeField(field) {
    if (!field || !field.name) return false;
    if (field.disabled) return false;
    if (field.name === 'bf_gender') return false;
    if (isMobile() && field.closest('.bf__drop--desktop')) return false;
    if (!isMobile() && field.closest('.bf__drop--mobile-filter')) return false;
    if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return false;
    if (field.type === 'number' && typeof field.value === 'string' && field.value.trim() === '') return false;
    return true;
  }

  function collectParams(form) {
    const params = new URLSearchParams();
    form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (!includeField(field)) return;
      if (typeof field.value === 'string' && field.value.trim() === '' && field.type !== 'checkbox') return;
      params.append(field.name, field.value);
    });
    return params;
  }

  function genderPath(form, gender) {
    let href = form.getAttribute('action') || window.location.pathname;
    if (gender === 'men') href = form.getAttribute('data-bf-men-url') || href;
    else if (gender === 'women') href = form.getAttribute('data-bf-women-url') || href;
    else if (gender === '') href = form.getAttribute('data-bf-clear-gender-url') || href;
    return new URL(href, window.location.origin);
  }

  function buildUrl(form, genderOverride) {
    const params = collectParams(form);
    const genderInput = form.querySelector('input[name="bf_gender"]:checked');
    const gender = genderOverride !== undefined ? genderOverride : genderInput ? genderInput.value : undefined;
    const base = gender !== undefined ? genderPath(form, gender) : new URL(window.location.pathname, window.location.origin);

    if (gender === undefined) {
      const actionPath = (form.getAttribute('action') || window.location.pathname).split('?')[0];
      let path = window.location.pathname.replace(/\/+$/, '');
      if (!path) path = actionPath.replace(/\/+$/, '');
      base.pathname = path;
    }

    params.forEach((value, key) => {
      base.searchParams.append(key, value);
    });

    const query = base.searchParams.toString();
    return query ? base.pathname.replace(/\/+$/, '') + '?' + query : base.pathname.replace(/\/+$/, '') || '/';
  }

  function mobileFilter(root) {
    return root ? root.querySelector('.bf__drop--mobile-filter') : null;
  }

  function setMobileFilterOpen(drop, open) {
    if (!drop) return;
    drop.open = !!open;
    const summary = drop.querySelector(':scope > summary');
    const panel = drop.querySelector('.bf__panel');
    if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.documentElement.classList.toggle('bf-mobile-filter-open', !!open);
    const navOpen = document.documentElement.classList.contains('baydoun-nav-open');
    document.body.style.overflow = open || navOpen ? 'hidden' : '';
  }

  function closeMobileFilter(root) {
    const drop = mobileFilter(root);
    if (drop) setMobileFilterOpen(drop, false);
    document.documentElement.classList.remove('bf-mobile-filter-open');
    if (!document.documentElement.classList.contains('baydoun-nav-open')) {
      document.body.style.overflow = '';
    }
  }

  function openKeys(root) {
    return Array.from(root.querySelectorAll('[data-bf-drop][open]'))
      .map((el) => el.getAttribute('data-bf-key'))
      .filter((key) => key && key !== 'mobile-filter');
  }

  function restoreOpen(root, keys) {
    keys.forEach((key) => {
      const drop = root.querySelector('[data-bf-drop][data-bf-key="' + CSS.escape(key) + '"]');
      if (drop) drop.open = true;
    });
  }

  function render(root, url, addToHistory, restoreKeys) {
    const sectionId = root.getAttribute(SECTION_ATTR);
    const grid = root.querySelector('[data-bf-results]');
    const keys = restoreKeys === false ? [] : openKeys(root);

    if (grid) grid.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    if (activeRequest) activeRequest.abort();
    const controller = new AbortController();
    activeRequest = controller;

    const fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);

    fetch(fetchUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Filter request failed: ' + response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const nextRoot = doc.querySelector('[' + SECTION_ATTR + ']');
        if (!nextRoot) throw new Error('Filter response missing section markup');

        ['[data-bf-bar]', '[data-bf-results]', '[data-bf-nav]'].forEach((selector) => {
          const current = root.querySelector(selector);
          const next = nextRoot.querySelector(selector);
          if (current && next) current.innerHTML = next.innerHTML;
        });

        restoreOpen(root, keys);
        closeMobileFilter(root);
        if (addToHistory) window.history.pushState({ baydounFacets: url }, '', url);
        root.dispatchEvent(new CustomEvent('baydoun:facets:rendered', { bubbles: true }));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
        window.location.href = url;
      })
      .finally(() => {
        if (activeRequest === controller) activeRequest = null;
        applying = false;
        const currentGrid = root.querySelector('[data-bf-results]');
        if (currentGrid) currentGrid.classList.remove('is-loading');
        root.removeAttribute('aria-busy');
      });
  }

  function submit(root, addToHistory, genderOverride) {
    const form = root.querySelector('[data-bf-form]');
    if (!form || applying) return;
    applying = true;
    render(root, buildUrl(form, genderOverride), addToHistory !== false);
  }

  function closeOthers(current) {
    const root = sectionRoot(current);
    if (!root) return;
    root.querySelectorAll('[data-bf-drop][open]').forEach((drop) => {
      if (drop !== current) drop.open = false;
    });
  }

  document.addEventListener('toggle', (event) => {
    const drop = event.target;
    if (!(drop instanceof HTMLDetailsElement) || !drop.matches('.bf__drop--mobile-filter')) return;
    setMobileFilterOpen(drop, drop.open);
    if (drop.open) {
      const closeBtn = drop.querySelector('[data-bf-close-mobile]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }
  }, true);

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-bf-form]');
    if (!form) return;
    if (event.target.type === 'number') return;
    if (event.target.closest('.bf__drop--mobile-filter')) return;
    const root = sectionRoot(form);
    if (root) submit(root);
  });

  document.addEventListener('click', (event) => {
    const closeMobile = event.target.closest('[data-bf-close-mobile]');
    if (closeMobile) {
      event.preventDefault();
      const root = sectionRoot(closeMobile);
      closeMobileFilter(root);
      const summary = root && mobileFilter(root) && mobileFilter(root).querySelector(':scope > summary');
      if (summary) summary.focus({ preventScroll: true });
      return;
    }

    const mobileDrop = event.target.closest('.bf__drop--mobile-filter');
    if (mobileDrop && event.target === mobileDrop) {
      event.preventDefault();
      setMobileFilterOpen(mobileDrop, false);
      const summary = mobileDrop.querySelector(':scope > summary');
      if (summary) summary.focus({ preventScroll: true });
      return;
    }

    const sortSet = event.target.closest('[data-bf-sort-set]');
    if (sortSet) {
      const root = sectionRoot(sortSet);
      if (!root) return;
      event.preventDefault();
      const form = root.querySelector('[data-bf-form]');
      const select = form && form.querySelector('[data-bf-sort]');
      const value = sortSet.getAttribute('data-bf-sort-set');
      if (select && value) {
        if (![...select.options].some((opt) => opt.value === value)) {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value;
          select.appendChild(opt);
        }
        select.value = value;
      }
      closeMobileFilter(root);
      root.querySelectorAll('[data-bf-drop][open]').forEach((drop) => {
        drop.open = false;
      });
      submit(root);
      return;
    }

    const apply = event.target.closest('[data-bf-apply]');
    if (apply) {
      event.preventDefault();
      const root = sectionRoot(apply);
      if (!root) return;
      closeMobileFilter(root);
      submit(root);
      return;
    }

    const reset = event.target.closest('[data-bf-reset-price]');
    if (reset) {
      event.preventDefault();
      const scope = reset.closest('.bf__drop--mobile-filter') || reset.closest('.bf__panel') || reset.closest('[data-bf-form]');
      if (scope) scope.querySelectorAll('input[type="number"]').forEach((input) => (input.value = ''));
      const root = sectionRoot(reset);
      if (root) {
        if (reset.closest('.bf__drop--mobile-filter')) closeMobileFilter(root);
        submit(root);
      }
      return;
    }

    const link = event.target.closest('[data-bf-link], [data-bf-pagination] a');
    if (link && link.href) {
      const root = sectionRoot(link);
      if (root) {
        event.preventDefault();
        closeMobileFilter(root);
        render(root, normalizeUrl(link.href), true, false);
        const results = root.querySelector('[data-bf-results]');
        if (results && link.matches('[data-bf-pagination] a, [data-bf-pagination] *')) {
          results.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return;
    }

    const summary = event.target.closest('[data-bf-drop] > summary');
    if (summary) {
      const drop = summary.parentElement;
      if (!drop.open) closeOthers(drop);
      return;
    }

    if (!event.target.closest('[data-bf-drop]')) {
      document.querySelectorAll('[data-bf-drop][open]').forEach((drop) => {
        if (drop.matches('.bf__drop--mobile-filter')) setMobileFilterOpen(drop, false);
        else drop.open = false;
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-bf-drop][open]').forEach((drop) => {
      if (drop.matches('.bf__drop--mobile-filter')) {
        setMobileFilterOpen(drop, false);
      } else {
        drop.open = false;
      }
      const summary = drop.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-bf-form]');
    if (!form) return;
    const root = sectionRoot(form);
    if (!root) return;
    event.preventDefault();
    closeMobileFilter(root);
    submit(root);
  });

  window.addEventListener('popstate', () => {
    document.querySelectorAll('[' + SECTION_ATTR + ']').forEach((root) => {
      closeMobileFilter(root);
      render(root, normalizeUrl(window.location.href), false, false);
    });
  });
})();
